// src/pages/KonuAtamaPage.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useProtectedPage } from '../hooks/useProtectedPage';
import { useAuth } from '../contexts/AuthContext';
import type { AtanmisKonu } from '../admin/hooks/useKonuAtama';
import { useKonuAtama } from '../admin/hooks/useKonuAtama';
import { Department } from '../admin/hooks/useDepartments';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Trash2, AlertTriangle, Eye } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useUniversalPreview } from '../hooks/useUniversalPreview';
import { KonuAtamaMobileView } from '../components/mobile/KonuAtamaMobileView';
import PanelHeader from '../components/PanelHeader';
import '../styles/PanelStyles.css';
import '../styles/Riskler.css';
import '../styles/anket-hazirla.css';
import '../styles/mobile/mobile-common.css';
import '../styles/mobile/konu-atama-mobile.css';

// Stil objeleri
const selectStyles = {
    control: (base: any) => ({ ...base, minHeight: '38px', zIndex: 10 }),
    menu: (base: any) => ({ ...base, zIndex: 20 }),
    option: (base: any, state: any) => ({
        ...base,
        color: '#000000 !important',
        backgroundColor: state.isSelected ? '#4CAF50' : state.isFocused ? '#E5F3E6' : 'white',
        '&:hover': {
            backgroundColor: '#E5F3E6',
            color: '#000000 !important'
        }
    }),
    singleValue: (base: any) => ({ ...base, color: '#000000 !important' }),
    placeholder: (base: any) => ({ ...base, color: '#6b7280 !important' })
};
const panelCardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'white',
  borderRadius: '0.375rem',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
};
// Panel başlık stili artık PanelStyles.css'den geliyor

const KonuAtamaPage: React.FC = () => {
  const { isReady, renderGuard, selectedFirma, selectedYil } = useProtectedPage();
  const { profile } = useAuth();
  
  const { 
    atanmamisKonular, 
    atanmisKonular, 
    bolumler, 
    isLoading, 
    error,
    assignKonularToBolum,
    unassignKonu,
    refetchData
  } = useKonuAtama(selectedFirma?.value || null, selectedYil);

  const [seciliKonular, setSeciliKonular] = useState<Set<number>>(new Set());
  const [seciliBolum, setSeciliBolum] = useState<{ value: number | string; label: string; data?: Department; __isNew__?: boolean } | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [kullanilmisBolumler, setKullanilmisBolumler] = useState<string[]>([]);
  const [benzerBolumUyarisi, setBenzerBolumUyarisi] = useState<string | null>(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [secilenBolumFiltresi, setSecilenBolumFiltresi] = useState<string | null>(null);
  const { preview } = useUniversalPreview();

  const handleKonuCheck = (konuId: number) => {
    const newSecimler = new Set(seciliKonular);
    if (newSecimler.has(konuId)) {
      newSecimler.delete(konuId);
    } else {
      newSecimler.add(konuId);
    }
    setSeciliKonular(newSecimler);
  };

  const handleTumunuSec = () => {
    const filtrelenmisKonular = filtreliAtanmamisKonular;
    const tumKonuIdleri = new Set(filtrelenmisKonular.map(k => k.id));
    setSeciliKonular(tumKonuIdleri);
  };

  const handleSecimiTemizle = () => {
    setSeciliKonular(new Set());
  };

  const handleBolumSil = async (bolumId: number, bolumAdi: string) => {
    // Sadece firmaya özel bölümleri sil
    const bolum = bolumler.find(b => b.id === bolumId);
    if (!bolum || !bolum.firma_adi || bolum.firma_adi !== selectedFirma?.value || bolum.raporlama_yili !== selectedYil) {
      toast.error('Sadece kendi oluşturduğunuz bölümleri silebilirsiniz.');
      return;
    }
    
    // Konu ataması var mı kontrol et
    const konuSayisi = bolumIstatistikleri[bolumAdi] || 0;
    if (konuSayisi > 0) {
      toast.error(`"${bolumAdi}" bölümünde ${konuSayisi} konu ataması var. Önce atamaları kaldırın.`);
      return;
    }
    
    const toastId = toast.loading(`"${bolumAdi}" bölümü siliniyor...`);
    try {
      const { error } = await supabase
        .from('bolumler')
        .delete()
        .eq('id', bolumId)
        .eq('firma_adi', selectedFirma?.value)
        .eq('raporlama_yili', selectedYil);
      
      if (error) throw error;
      
      toast.success(`"${bolumAdi}" bölümü silindi.`, { id: toastId });
      
      // Seçili bölüm silinmişse temizle
      if (seciliBolum?.value === bolumId) {
        setSeciliBolum(null);
      }
      
      // Veriyi yenile
      await refetchData();
    } catch (err: any) {
      toast.error(`Bölüm silinemedi: ${err.message}`, { id: toastId });
    }
  };

  const handleAtamaYap = async () => {
    if (seciliKonular.size === 0 || !seciliBolum) {
      toast.error("Lütfen atanacak konuları ve sorumlu bölümü seçin.");
      return;
    }
    
    setIsAssigning(true);
    const toastId = toast.loading(`${seciliKonular.size} konu '${seciliBolum.label}' bölümüne atanıyor...`);
    
    try {
      const bolumId = seciliBolum.value;
      
      // Bölüm ID'si sayısal olmalı (yeni bölümler artık onCreateOption'da oluşturuluyor)
      if (typeof bolumId !== 'number') {
        throw new Error('Geçersiz bölüm seçimi');
      }
      
      await assignKonularToBolum(Array.from(seciliKonular), bolumId);
      toast.success("Atama başarılı!", { id: toastId });
      setSeciliKonular(new Set());
      setSeciliBolum(null);
      setBenzerBolumUyarisi(null);
    } catch (err: any) {
      toast.error(`Hata: ${err.message}`, { id: toastId });
    } finally {
      setIsAssigning(false);
    }
  };
  
  const handleUnassignClick = async (konu: AtanmisKonu) => {
    const toastId = toast.loading(`'${konu.konu}' ataması geri alınıyor...`);
    try {
      await unassignKonu(konu);
      toast.success("Atama başarıyla geri alındı.", { id: toastId });
    } catch (err: any) {
      toast.error(`Hata: ${err.message}`, { id: toastId });
    }
  };

  // Kullanılmış bölümleri getir
  useEffect(() => {
    const fetchKullanilmisBolumler = async () => {
      if (!selectedFirma?.value || !selectedYil) return;
      
      try {
        // Atanmış konulardan bölüm adlarını al
        const atanmisBolumler = [...new Set(atanmisKonular.map(k => k.bolum_adi))];
        setKullanilmisBolumler(atanmisBolumler);
      } catch (err) {
        console.error('Kullanılmış bölümler getirilemedi:', err);
      }
    };
    
    fetchKullanilmisBolumler();
  }, [selectedFirma?.value, selectedYil, atanmisKonular]);

  // Benzer bölüm kontrolü
  const checkBenzerBolum = (inputValue: string) => {
    if (!inputValue || inputValue.length < 2) {
      setBenzerBolumUyarisi(null);
      return;
    }
    
    const normalizedInput = inputValue.toLowerCase().trim();
    const benzerler = kullanilmisBolumler.filter(bolum => {
      const normalized = bolum.toLowerCase().trim();
      return normalized !== normalizedInput && 
             (normalized.includes(normalizedInput) || 
              normalizedInput.includes(normalized) ||
              levenshteinDistance(normalized, normalizedInput) <= 2);
    });
    
    if (benzerler.length > 0) {
      setBenzerBolumUyarisi(`Benzer bölümler mevcut: ${benzerler.join(', ')}`);
    } else {
      setBenzerBolumUyarisi(null);
    }
  };

  // Levenshtein distance hesaplama
  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };

  // Filtrelenmiş atanmamış konular
  const filtreliAtanmamisKonular = useMemo(() => {
    return atanmamisKonular.filter(konu => {
      const aramaUygun = !aramaMetni || 
        konu.konu.toLowerCase().includes(aramaMetni.toLowerCase()) ||
        konu.standart_detay.toLowerCase().includes(aramaMetni.toLowerCase());
      return aramaUygun;
    });
  }, [atanmamisKonular, aramaMetni]);

  // Filtrelenmiş atanmış konular
  const filtreliAtanmisKonular = useMemo(() => {
    return atanmisKonular.filter(konu => {
      const bolumUygun = !secilenBolumFiltresi || konu.bolum_adi === secilenBolumFiltresi;
      const aramaUygun = !aramaMetni || 
        konu.konu.toLowerCase().includes(aramaMetni.toLowerCase()) ||
        konu.standart_detay.toLowerCase().includes(aramaMetni.toLowerCase());
      return bolumUygun && aramaUygun;
    });
  }, [atanmisKonular, secilenBolumFiltresi, aramaMetni]);

  // Bölüm istatistikleri
  const bolumIstatistikleri = useMemo(() => {
    const stats: Record<string, number> = {};
    atanmisKonular.forEach(konu => {
      stats[konu.bolum_adi] = (stats[konu.bolum_adi] || 0) + 1;
    });
    return stats;
  }, [atanmisKonular]);

  // Bölüm seçenekleri (genel + firmaya özel)
  const bolumOptions = useMemo(() => {
    // Genel bölümler (firma_adi null olanlar)
    const genelBolumler = bolumler
      .filter(b => !b.firma_adi)
      .map(b => ({ 
        value: b.id, 
        label: `${b.bolum_adi} ${bolumIstatistikleri[b.bolum_adi] ? `(${bolumIstatistikleri[b.bolum_adi]} konu)` : ''}`, 
        data: b,
        isGeneral: true,
        canDelete: false
      }));
    
    // Firmaya özel bölümler
    const firmaOzelBolumler = bolumler
      .filter(b => b.firma_adi === selectedFirma?.value && b.raporlama_yili === selectedYil)
      .map(b => ({ 
        value: b.id, 
        label: `${b.bolum_adi} ${bolumIstatistikleri[b.bolum_adi] ? `(${bolumIstatistikleri[b.bolum_adi]} konu)` : ''} 🏢`, 
        data: b,
        isGeneral: false,
        canDelete: true
      }));
    
    // Kullanılmış bölümler
    const kullanilmisOptions = kullanilmisBolumler
      .filter(bolum => !bolumler.some(b => b.bolum_adi === bolum))
      .map(bolum => ({ 
        value: `custom_${bolum}`, 
        label: `${bolum} (${bolumIstatistikleri[bolum] || 0} konu - Önceden kullanılmış)`, 
        data: undefined,
        isGeneral: false,
        canDelete: false
      }));
    
    return [...genelBolumler, ...firmaOzelBolumler, ...kullanilmisOptions];
  }, [bolumler, kullanilmisBolumler, bolumIstatistikleri, selectedFirma?.value, selectedYil]);

  // Benzersiz bölüm adları (filtre için)
  const benzersizBolumAdlari = useMemo(() => {
    return [...new Set(atanmisKonular.map(k => k.bolum_adi))].sort();
  }, [atanmisKonular]);



  const downloadAsCSV = () => {
    try {
      const csvData = [
        ['S.No', 'Konu', 'Standart', 'Sorumlu Bölüm']
      ];
      
      atanmisKonular.forEach((konu, index) => {
        csvData.push([index + 1, `"${konu.konu}"`, `"${konu.standart_detay}"`, `"${konu.bolum_adi}"`]);
      });
      
      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${selectedFirma?.label || 'Firma'}_Konu_Atamalari_${selectedYil}.csv`;
      link.click();
      toast.success('CSV dosyası indirildi!');
    } catch (error) {
      console.error('CSV indirme hatası:', error);
      toast.error('CSV indirme başarısız!');
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return <p style={{ textAlign: 'center', padding: '2rem' }}>Konu verileri yükleniyor...</p>;
    }
    if (error) {
      return <p style={{ color: 'red', textAlign: 'center', padding: '2rem' }}>Hata: {error}</p>;
    }
    if (!isReady) {
      return renderGuard();
    }
    
    return (
      <div>
        {/* Mobil Görünüm */}
        <KonuAtamaMobileView
          atanmamisKonular={atanmamisKonular}
          filtreliAtanmamisKonular={filtreliAtanmamisKonular}
          seciliKonular={seciliKonular}
          onKonuCheck={handleKonuCheck}
          onTumunuSec={handleTumunuSec}
          onSecimiTemizle={handleSecimiTemizle}
          aramaMetni={aramaMetni}
          onAramaChange={setAramaMetni}
          seciliBolum={seciliBolum}
          onBolumChange={(opt) => {
            setSeciliBolum(opt);
            setBenzerBolumUyarisi(null);
          }}
          onBolumCreate={async (inputValue) => {
            if (!selectedFirma?.value || !selectedYil) {
              toast.error('Firma ve yıl seçili değil');
              return;
            }
            
            const toastId = toast.loading(`"${inputValue}" bölümü oluşturuluyor...`);
            
            try {
              const { data: newBolum, error: bolumError } = await supabase
                .from('bolumler')
                .insert({
                  bolum_adi: inputValue,
                  firma_adi: selectedFirma.value,
                  raporlama_yili: selectedYil
                })
                .select()
                .single();
              
              if (bolumError) throw new Error('Yeni bölüm oluşturulamadı: ' + bolumError.message);
              
              toast.success(`"${inputValue}" bölümü oluşturuldu!`, { id: toastId });
              
              const newOption = {
                value: newBolum.id,
                label: inputValue,
                data: newBolum
              };
              setSeciliBolum(newOption);
              await refetchData();
              checkBenzerBolum(inputValue);
            } catch (err: any) {
              toast.error(`Bölüm oluşturulamadı: ${err.message}`, { id: toastId });
            }
          }}
          bolumOptions={bolumOptions}
          benzerBolumUyarisi={benzerBolumUyarisi}
          bolumler={bolumler}
          selectedFirma={selectedFirma}
          selectedYil={selectedYil}
          bolumIstatistikleri={bolumIstatistikleri}
          onBolumSil={handleBolumSil}
          kullanilmisBolumler={kullanilmisBolumler}
          atanmisKonular={atanmisKonular}
          filtreliAtanmisKonular={filtreliAtanmisKonular}
          secilenBolumFiltresi={secilenBolumFiltresi}
          onBolumFiltresiChange={setSecilenBolumFiltresi}
          benzersizBolumAdlari={benzersizBolumAdlari}
          onUnassignClick={handleUnassignClick}
          isAssigning={isAssigning}
          onAtamaYap={handleAtamaYap}
          onPreview={() => {
            const tableData = atanmisKonular.map((konu, index) => ({
              'S.No': index + 1,
              'Konu': konu.konu,
              'Standart': konu.standart_detay,
              'Sorumlu Bölüm': konu.bolum_adi
            }));
            preview({
              title: `${selectedFirma?.label || 'Firma'}\n${selectedYil} Yılı Sürdürülebilirlik Raporu\nKonu Atama`,
              data: tableData,
              companyName: selectedFirma?.label || 'Firma',
              year: selectedYil || new Date().getFullYear(),
              pageTitle: 'Konu Atama',
              columns: [
                { key: 'S.No', title: 'S.No', width: '10%' },
                { key: 'Konu', title: 'Konu', width: '40%' },
                { key: 'Standart', title: 'Standart', width: '25%' },
                { key: 'Sorumlu Bölüm', title: 'Sorumlu Bölüm', width: '25%' }
              ]
            });
          }}
        />

                {/* Desktop Görünüm */}
        <div className="konu-atama-desktop-layout desktop-only">
          <PanelHeader
            title="Konu Atama"
            className="page-theme-header konu-atama-page-header shadow-lg"
            align="left"
            variant="large"
            actions={
              <button 
                onClick={() => {
                  const tableData = atanmisKonular.map((konu, index) => ({
                    'S.No': index + 1,
                    'Konu': konu.konu,
                    'Standart': konu.standart_detay,
                    'Sorumlu Bölüm': konu.bolum_adi
                  }));
                  preview({
                    title: `${selectedFirma?.label || 'Firma'}\n${selectedYil} Yılı Sürdürülebilirlik Raporu\nKonu Atama`,
                    data: tableData,
                    companyName: selectedFirma?.label || 'Firma',
                    year: selectedYil || new Date().getFullYear(),
                    pageTitle: 'Konu Atama',
                    columns: [
                      { key: 'S.No', title: 'S.No', width: '10%' },
                      { key: 'Konu', title: 'Konu', width: '40%' },
                      { key: 'Standart', title: 'Standart', width: '25%' },
                      { key: 'Sorumlu Bölüm', title: 'Sorumlu Bölüm', width: '25%' }
                    ]
                  });
                }} 
                disabled={atanmisKonular.length === 0}
                className={`header-action-btn flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  atanmisKonular.length === 0
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                    : 'header-action-btn--glass text-white'
                }`}
              >
                <Eye size={14} />
                <span>Önizle</span>
              </button>
            }
          />
        <div className="konu-atama-panel-grid">
        
        {/* Sütun 1: Atanmamış Konular */}
        <div className="card theme-card-shell konu-atama-panel konu-atama-panel--left" style={{ background: 'white' }}>
          <div className="card-header section-theme-header section-theme-header--compact konu-atama-section-header">
            <h2 className="card-title text-sm">
              <span className="text-lg">📋</span>
              Atanmamış Konular ({filtreliAtanmamisKonular.length}/{atanmamisKonular.length})
            </h2>
          </div>
          
          {/* Arama ve Toplu İşlemler */}
          <div className="theme-filter-strip" style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', backgroundColor: 'rgba(255,255,255,0.9)' }}>
            <input
              type="text"
              placeholder="Konu ara..."
              value={aramaMetni}
              onChange={(e) => setAramaMetni(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                marginBottom: '0.5rem'
              }}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleTumunuSec}
                disabled={filtreliAtanmamisKonular.length === 0}
                style={{
                  flex: 1,
                  padding: '0.375rem 0.5rem',
                  fontSize: '0.75rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: filtreliAtanmamisKonular.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: filtreliAtanmamisKonular.length === 0 ? 0.5 : 1
                }}
              >
                Tümünü Seç
              </button>
              <button
                onClick={handleSecimiTemizle}
                disabled={seciliKonular.size === 0}
                style={{
                  flex: 1,
                  padding: '0.375rem 0.5rem',
                  fontSize: '0.75rem',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: seciliKonular.size === 0 ? 'not-allowed' : 'pointer',
                  opacity: seciliKonular.size === 0 ? 0.5 : 1
                }}
              >
                Temizle
              </button>
            </div>
          </div>
          
          <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', minHeight: 0, maxHeight: 'calc(70vh - 140px)' }}>
            {filtreliAtanmamisKonular.length === 0 ? 
              <p style={{padding: '1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem'}}>
                {aramaMetni ? 'Arama kriterine uygun konu bulunamadı.' : 'Atanacak yeni konu yok.'}
              </p> :
              filtreliAtanmamisKonular.map(konu => (
                <div key={`atanmamis-${konu.id}`} style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ position: 'relative', display: 'inline-block', marginTop: '2px', flexShrink: 0 }}>
                    <input 
                      type="checkbox" 
                      id={`konu-${konu.id}`} 
                      checked={seciliKonular.has(konu.id)} 
                      onChange={() => handleKonuCheck(konu.id)} 
                      style={{
                        opacity: 0,
                        position: 'absolute',
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                    <label 
                      htmlFor={`konu-${konu.id}`} 
                      style={{
                        display: 'inline-block',
                        width: '18px',
                        height: '18px',
                        border: '2px solid #d1d5db',
                        borderRadius: '4px',
                        background: seciliKonular.has(konu.id) ? '#4CAF50' : 'white',
                        borderColor: seciliKonular.has(konu.id) ? '#4CAF50' : '#d1d5db',
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {seciliKonular.has(konu.id) && (
                        <span style={{
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          lineHeight: 1
                        }}>✓</span>
                      )}
                    </label>
                  </div>
                  <label htmlFor={`konu-${konu.id}`} style={{ cursor: 'pointer', flex: 1, fontSize: '0.9rem', lineHeight: '1.4' }}>
                    <span style={{ fontWeight: '500', color: '#374151' }}>{konu.konu}</span>
                    <span style={{ color: '#6b7280', fontSize: '0.85em', marginLeft: '4px' }}>({konu.standart_detay})</span>
                  </label>
                </div>
              ))
            }
          </div>
        </div>

        {/* Sütun 2: İşlem Paneli - NİHAİ YAPI */}
        <div className="card theme-card-shell konu-atama-panel konu-atama-panel--middle" style={{ background: 'white' }}>
          <div className="card-header section-theme-header section-theme-header--compact konu-atama-section-header">
            <h2 className="card-title text-sm">
              <span className="text-lg">⚙️</span>
              İşlem
            </h2>
          </div>
          <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
            
            <div>
              <label htmlFor="bolum-select" style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Sorumlu Bölüm:</label>
              <CreatableSelect 
                inputId="bolum-select" 
                options={bolumOptions} 
                value={seciliBolum} 
                onChange={(opt) => {
                  console.log('🔍 CreatableSelect onChange:', opt);
                  setSeciliBolum(opt);
                  setBenzerBolumUyarisi(null);
                }} 
                onCreateOption={async (inputValue) => {
                  console.log('🆕 onCreateOption çağrıldı:', inputValue);
                  
                  if (!selectedFirma?.value || !selectedYil) {
                    toast.error('Firma ve yıl seçili değil');
                    return;
                  }
                  
                  const toastId = toast.loading(`"${inputValue}" bölümü oluşturuluyor...`);
                  
                  try {
                    // Veritabanına kaydet
                    const { data: newBolum, error: bolumError } = await supabase
                      .from('bolumler')
                      .insert({
                        bolum_adi: inputValue,
                        firma_adi: selectedFirma.value,
                        raporlama_yili: selectedYil
                      })
                      .select()
                      .single();
                    
                    if (bolumError) {
                      console.error('❌ Bölüm oluşturma hatası:', bolumError);
                      throw new Error('Yeni bölüm oluşturulamadı: ' + bolumError.message);
                    }
                    
                    console.log('✅ Yeni bölüm oluşturuldu:', newBolum);
                    toast.success(`"${inputValue}" bölümü oluşturuldu!`, { id: toastId });
                    
                    // State'i güncelle
                    const newOption = {
                      value: newBolum.id,
                      label: inputValue,
                      data: newBolum
                    };
                    setSeciliBolum(newOption);
                    
                    // Veriyi yenile (dropdown ve "Kendi Bölümleriniz" listesi güncellensin)
                    await refetchData();
                    
                    checkBenzerBolum(inputValue);
                  } catch (err: any) {
                    console.error('❌ onCreateOption hatası:', err);
                    toast.error(`Bölüm oluşturulamadı: ${err.message}`, { id: toastId });
                  }
                }}
                onInputChange={(inputValue, actionMeta) => {
                  console.log('⌨️ Input değişti:', inputValue, 'Action:', actionMeta.action);
                  checkBenzerBolum(inputValue);
                }}
                isValidNewOption={(inputValue, selectValue, selectOptions) => {
                  const isValid = inputValue && inputValue.trim().length > 0 && 
                    !selectOptions.some((opt: any) => opt.label?.toLowerCase() === inputValue.toLowerCase());
                  console.log('✅ isValidNewOption:', inputValue, '→', isValid);
                  return isValid || '';
                }}
                placeholder="Bölüm seçin veya yeni bölüm adı yazın..." 
                isClearable
                createOptionPosition="first"
                allowCreateWhileLoading={true}
                formatCreateLabel={(inputValue) => `"➡️ ${inputValue}" bölümünü oluştur (Enter)`}
                noOptionsMessage={() => "Yeni bölüm adı yazıp Enter'a basın"}
                styles={{
                  ...selectStyles,
                  option: (base: any, state: any) => ({
                    ...base,
                    color: '#000000 !important',
                    backgroundColor: state.data?.__isNew__
                      ? (state.isSelected ? '#10b981' : state.isFocused ? '#d1fae5' : '#ecfdf5')
                      : state.data?.isGeneral
                      ? (state.isSelected ? '#059669' : state.isFocused ? '#ECFDF5' : '#F0FDF4')
                      : state.data?.label?.includes('Önceden kullanılmış') 
                        ? (state.isSelected ? '#4CAF50' : state.isFocused ? '#FFF3CD' : '#FFFBF0')
                        : (state.isSelected ? '#4CAF50' : state.isFocused ? '#E5F3E6' : 'white'),
                    fontStyle: state.data?.label?.includes('Önceden kullanılmış') ? 'italic' : 'normal',
                    fontWeight: state.data?.__isNew__ ? 'bold' : 'normal'
                  })
                }}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    neutral80: '#000000',
                    neutral90: '#000000'
                  }
                })}
              />
              
              {benzerBolumUyarisi && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#FEF3C7',
                  border: '1px solid #F59E0B',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem'
                }}>
                  <AlertTriangle size={16} color="#D97706" />
                  <span style={{ color: '#92400E' }}>{benzerBolumUyarisi}</span>
                </div>
              )}
              
              {/* Firmaya Özel Bölümler Yönetimi - Her Zaman Görünür */}
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>Kendi Bölümleriniz:</h4>
                {(() => {
                  const firmaBolumleri = bolumler.filter(b => b.firma_adi === selectedFirma?.value && b.raporlama_yili === selectedYil);
                  
                  if (firmaBolumleri.length === 0) {
                    return (
                      <div style={{ padding: '8px', textAlign: 'center', color: '#6b7280', fontSize: '0.8rem', fontStyle: 'italic' }}>
                        Eğer firmanızın farklı bölümleri varsa, yukarıda Bölüm Adını girerek oluşturabilirsiniz.
                      </div>
                    );
                  }
                  
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {firmaBolumleri.map(bolum => (
                        <div key={bolum.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                          <span style={{ fontSize: '0.8rem', color: '#374151' }}>
                            🏢 {bolum.bolum_adi} {bolumIstatistikleri[bolum.bolum_adi] ? `(${bolumIstatistikleri[bolum.bolum_adi]} konu)` : ''}
                          </span>
                          <button
                            onClick={() => handleBolumSil(bolum.id, bolum.bolum_adi)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#ef4444',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title={`"${bolum.bolum_adi}" bölümünü sil`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              
              <div style={{ marginTop: '12px', fontSize: '0.75rem', color: '#6B7280' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                  <span style={{ color: '#059669' }}>●</span>
                  <span>Yeşil: Genel bölümler (tüm firmalar kullanabilir)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>🏢</span>
                  <span>Firmaya özel bölümler (sadece siz görebilirsiniz)</span>
                </div>
              </div>
              
              {kullanilmisBolumler.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <p style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '6px', fontWeight: '500' }}>Bu firma için kullanılmış bölümler:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {kullanilmisBolumler.slice(0, 5).map((bolum, index) => (
                      <span 
                        key={index}
                        onClick={() => setSeciliBolum({ value: `custom_${bolum}`, label: bolum })}
                        style={{
                          fontSize: '0.75rem',
                          backgroundColor: '#E5F3E6',
                          color: '#166534',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          border: '1px solid #BBF7D0'
                        }}
                        className="hover:bg-green-200"
                      >
                        {bolum}
                      </span>
                    ))}
                    {kullanilmisBolumler.length > 5 && (
                      <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>+{kullanilmisBolumler.length - 5} daha...</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Boşluk elemanı, butonu en alta iter */}
            <div style={{ flexGrow: 1 }}></div>

            {/* Buton her zaman render edilir, sadece disabled durumu değişir */}
            <button 
              className="btn-success header-action-btn header-action-btn--accent w-full"
              onClick={handleAtamaYap} 
              disabled={isAssigning || seciliKonular.size === 0 || !seciliBolum}
            >
              {isAssigning ? 'Atanıyor...' : `Kaydet (${seciliKonular.size})`}
            </button>
            
          </div>
        </div>

        {/* Sütun 3: Atanmış Konular */}
        <div className="card theme-card-shell konu-atama-panel konu-atama-panel--right" style={{ background: 'white' }}>
           <div className="card-header section-theme-header section-theme-header--compact konu-atama-section-header">
             <h2 className="card-title text-sm">
               <span className="text-lg">✅</span>
               Atanmış Konular ({filtreliAtanmisKonular.length}/{atanmisKonular.length})
             </h2>
           </div>
           
           {/* Bölüm Filtresi */}
           <div className="theme-filter-strip" style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', backgroundColor: 'rgba(255,255,255,0.9)' }}>
             <select
               value={secilenBolumFiltresi || ''}
               onChange={(e) => setSecilenBolumFiltresi(e.target.value || null)}
               style={{
                 width: '100%',
                 padding: '0.5rem',
                 border: '1px solid #d1d5db',
                 borderRadius: '0.375rem',
                 fontSize: '0.875rem',
                 backgroundColor: 'white'
               }}
             >
               <option value="">Tüm Bölümler</option>
               {benzersizBolumAdlari.map(bolum => (
                 <option key={bolum} value={bolum}>
                   {bolum} ({bolumIstatistikleri[bolum]} konu)
                 </option>
               ))}
             </select>
           </div>
           
           <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, maxHeight: 'calc(70vh - 120px)' }}>
              {filtreliAtanmisKonular.length === 0 ? 
                <p style={{padding: '1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem'}}>
                  {secilenBolumFiltresi || aramaMetni ? 'Filtre kriterlerine uygun konu bulunamadı.' : 'Henüz atanmış konu yok.'}
                </p> :
                <table style={{width: '100%', fontSize: '0.9em', borderCollapse: 'collapse'}}>
                  <thead className="bg-gradient-to-r from-indigo-50 to-purple-50 sticky top-0 z-10">
                    <tr style={{textAlign: 'left'}}>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-indigo-900 border-b-2 border-indigo-200">Konu</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-indigo-900 border-b-2 border-indigo-200">Sorumlu Bölüm</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtreliAtanmisKonular.map((konu) => (
                      <tr key={`atanmis-${konu.id}`}>
                          <td style={{padding: '10px 12px', borderTop: '1px solid #eee', display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
                              <button 
                                onClick={() => handleUnassignClick(konu)}
                                title="Atamayı Geri Al"
                                style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '2px' }}
                              >
                                <Trash2 size={14} />
                              </button>
                              <span style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                                <span style={{ fontWeight: '500', color: '#374151' }}>{konu.konu}</span>
                                <span style={{ color: '#6b7280', fontSize: '0.85em', marginLeft: '4px' }}>({konu.standart_detay})</span>
                              </span>
                          </td>
                          <td style={{padding: '10px 12px', borderTop: '1px solid #eee', verticalAlign: 'top'}}>
                              <strong>{konu.bolum_adi}</strong>
                          </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              }
           </div>
        </div>
        </div>
        </div>
      </div>
    );
  };



  return (
    <div>
      {renderContent()}
      

    </div>
  );
};

export default KonuAtamaPage;



